import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reservationService, productService, userService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import { buildGoMessagesPath } from '../utils/redirect';

const STATUS_ICONS = {
  ACTIVE:    { icon: 'pending',       color: 'text-primary' },
  ACCEPTED:  { icon: 'check_circle',  color: 'text-on-surface-variant/40', fill: true },
  REJECTED:  { icon: 'cancel',        color: 'text-error-dim' },
  WITHDRAWN: { icon: 'cancel',        color: 'text-error-dim' },
  EXPIRED:   { icon: 'schedule',      color: 'text-on-surface-variant/40' },
};

function ReservationsNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = getThemeVars(ACTIVE_THEME);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('buyer'); // 'buyer' | 'seller' | 'history'
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showAll, setShowAll] = useState(false);
  // ID de la reserva a "linkear desde fuera" — ej. el click en la tarjeta
  // de reserva del chat manda ?reservationId=X. La marcamos visualmente
  // por unos segundos y nos posicionamos en su tab + sin paginar fuera.
  const [highlightedId, setHighlightedId] = useState(null);
  const highlightedRowRef = useRef(null);
  const INITIAL_LIMIT = 10;
  const HISTORY_HIDDEN_KEY = 'reservations.hiddenHistoryIds';
  const [hiddenHistoryIds, setHiddenHistoryIds] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_HIDDEN_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const HISTORY_STATUSES = new Set(['ACCEPTED', 'REJECTED', 'WITHDRAWN', 'EXPIRED']);

  const STATUS_CONFIG = {
    ACTIVE:    { label: t('Pendiente'),  ...STATUS_ICONS.ACTIVE },
    ACCEPTED:  { label: t('Completada'), ...STATUS_ICONS.ACCEPTED },
    REJECTED:  { label: t('Rechazada'),  ...STATUS_ICONS.REJECTED },
    WITHDRAWN: { label: t('Cancelada'),  ...STATUS_ICONS.WITHDRAWN },
    EXPIRED:   { label: t('Expirada'),   ...STATUS_ICONS.EXPIRED },
  };

  const currentUserId = user?.id;

  // ── Fetch reservations + enrich with product data ───────────────────
  const { data: reservations = [], isLoading } = useQuery({
    queryKey: ['reservations'],
    queryFn: async () => {
      const r = await reservationService.getMyReservations();
      const list = r.data || [];
      if (!Array.isArray(list)) return [];

      // Enrich each reservation with product + partner name. La contraparte
      // depende del rol: si soy buyer → seller del producto; si soy seller
      // → buyer (res.userId). El chat con la contraparte se abre desde un
      // único botón que ya viaja con reservationId; ya no traemos teléfono
      // ni email — comunicamos por el chat interno.
      const enriched = await Promise.all(list.map(async (res) => {
        const productId = res.productId;
        let product = null;
        let sellerName = null;
        let partnerName = null;

        if (productId) {
          try {
            const pRes = await productService.getProductById(productId);
            product = pRes.data;
          } catch { /* product might be deleted */ }
        }

        const iAmBuyer  = res.userId === currentUserId;
        const partnerId = iAmBuyer ? product?.publisherId : res.userId;

        if (product?.publisherId) {
          try {
            const nRes = await userService.getUserName(product.publisherId);
            sellerName = nRes.data;
          } catch { /* fallback below */ }
        }

        if (partnerId) {
          try {
            const uRes = await userService.getUserById(partnerId);
            partnerName = uRes.data?.name || partnerName;
          } catch { /* fallback below */ }
        }

        return { ...res, product, sellerName, partnerName, partnerId };
      }));

      return enriched;
    },
    enabled: !!user,
  });

  // ── Split into buyer/seller/history ─────────────────────────────────
  const { buyerReservations, sellerReservations, historyReservations, metrics } = useMemo(() => {
    const buyer = [];
    const seller = [];
    const history = [];
    let active = 0, completed = 0, cancelled = 0;

    reservations.forEach(r => {
      const isBuyer = r.userId === currentUserId;
      const isSeller = r.product?.publisherId === currentUserId;
      const isHistory = HISTORY_STATUSES.has(r.status);
      if (isHistory) {
        if (!hiddenHistoryIds.has(r.id)) history.push(r);
      } else {
        if (isBuyer) buyer.push(r);
        if (isSeller) seller.push(r);
      }

      if (r.status === 'ACTIVE') active++;
      else if (r.status === 'ACCEPTED') completed++;
      else if (r.status === 'WITHDRAWN' || r.status === 'REJECTED') cancelled++;
    });

    return { buyerReservations: buyer, sellerReservations: seller, historyReservations: history, metrics: { active, completed, cancelled } };
  }, [reservations, currentUserId, hiddenHistoryIds]);

  const currentList = tab === 'buyer' ? buyerReservations : tab === 'seller' ? sellerReservations : historyReservations;
  const filtered = statusFilter === 'ALL' ? currentList : currentList.filter(r => r.status === statusFilter);
  const visibleList = showAll ? filtered : filtered.slice(0, INITIAL_LIMIT);
  const hasMore = filtered.length > INITIAL_LIMIT;

  // ── Mutations ───────────────────────────────────────────────────────
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['reservations'] });
  const withdrawMutation = useMutation({ mutationFn: (id) => reservationService.withdrawReservation(id), onSuccess: invalidate });
  const acceptMutation = useMutation({ mutationFn: (id) => reservationService.acceptReservation(id), onSuccess: invalidate });
  const rejectMutation = useMutation({ mutationFn: (id) => reservationService.rejectReservation(id), onSuccess: invalidate });

  // ── Helpers ─────────────────────────────────────────────────────────
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    if (Array.isArray(dateValue)) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
      return new Date(year, month - 1, day, hour, minute, second);
    }
    return new Date(dateValue);
  };

  const timeAgo = (dateValue) => {
    const d = parseDate(dateValue);
    if (!d || isNaN(d.getTime())) return '';
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return `${Math.floor(diffH / 24)}d ago`;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  // ── Deep-link a una reserva específica ─────────────────────────────
  // El chat (y eventualmente otros lugares) puede enlazar a
  // /reservations?reservationId=X. Cuando entramos por ese link:
  //  1. Saltamos al tab apropiado (history si la reserva está finalizada;
  //     buyer/seller según el rol del user en esa reserva).
  //  2. Limpiamos filtros y expandimos paginación para que la fila sea
  //     visible aunque esté pasada el INITIAL_LIMIT.
  //  3. Marcamos la fila con ring + scroll, y limpiamos el highlight
  //     después de un par de segundos para no dejar la UI "tatuada".
  //  4. Removemos el query param para que un refresh / back no
  //     re-disparen el highlight.
  useEffect(() => {
    const targetIdRaw = searchParams.get('reservationId');
    if (!targetIdRaw || reservations.length === 0) return;
    const match = reservations.find((r) => String(r.id) === String(targetIdRaw));
    if (!match) {
      // La reserva existe del lado backend pero no está en este user — lo
      // único razonable es dejar la URL limpia y no marcar nada.
      setSearchParams({}, { replace: true });
      return;
    }

    let targetTab;
    if (HISTORY_STATUSES.has(match.status)) {
      targetTab = 'history';
    } else if (match.userId === currentUserId) {
      targetTab = 'buyer';
    } else if (match.product?.publisherId === currentUserId) {
      targetTab = 'seller';
    } else {
      targetTab = 'buyer';
    }
    setTab(targetTab);
    setStatusFilter('ALL');
    setShowAll(true);
    setHighlightedId(match.id);
    setSearchParams({}, { replace: true });

    // 2s — coincide con la duración de la animación CSS abajo, que arranca
    // saturada y va fadeando a transparent.
    const timeoutId = setTimeout(() => setHighlightedId(null), 2000);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, reservations.length]);

  // Scroll hacia la fila highlighted una vez que está montada en el DOM.
  // Necesita correr DESPUÉS de que el tab matcheante se renderee, por
  // eso depende también de `tab`.
  useEffect(() => {
    if (!highlightedId || !highlightedRowRef.current) return;
    highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedId, tab]);

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      {/* Keyframe del highlight usado al deep-linkear una reserva desde
          el chat. 2s con fade. Definido inline porque es el único lugar
          de la app donde se usa — no justifica un archivo CSS aparte. */}
      <style>{`
        @keyframes cm-row-highlight-fade {
          0%   { background-color: rgb(var(--cm-primary) / 0.18); }
          100% { background-color: rgb(var(--cm-primary) / 0); }
        }
      `}</style>
      <DashboardSidebar activePath="/reservations" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-6xl mx-auto">

          {/* ── Header & Metrics ── */}
          <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-end mobile-sm:justify-between gap-6 mb-8">
            <div className="flex flex-col">
              <h1 className="text-3xl font-black text-on-surface tracking-tight mb-1">{t('Mis compraventas')}</h1>
              <p className="text-on-surface-variant text-sm font-medium">{t('Gestiona tus compraventas en comunidades')}</p>
            </div>
            <div className="w-full mobile-sm:w-auto">
              <div className="bg-surface-container px-5 py-3 rounded-xl grid grid-cols-2 mobile-sm:flex items-center gap-4">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">{t('Activas')}</span>
                  <span className="text-xl font-black text-primary">{metrics.active}</span>
                </div>
                <div className="w-[1px] h-8 bg-outline-variant/20" />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">{t('Completadas')}</span>
                  <span className="text-xl font-black text-secondary">{metrics.completed}</span>
                </div>
                <div className="w-[1px] h-8 bg-outline-variant/20" />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">{t('Canceladas')}</span>
                  <span className="text-xl font-black text-error-dim">{metrics.cancelled}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Filter Bar ── */}
          <div className="flex flex-col mobile-md:flex-row mobile-md:items-center mobile-md:justify-between gap-3 mb-6 bg-surface border border-outline p-2 rounded-xl">
            <div className="flex p-1 bg-surface-container rounded-xl w-full mobile-sm:w-fit">
              <a
                onClick={() => { setTab('buyer'); setShowAll(false); }}
                className={`flex-1 mobile-sm:flex-none px-6 py-1.5 rounded-lg text-center text-sm font-bold cursor-pointer transition-colors ${tab === 'buyer' ? 'bg-surface-container-lowest text-primary shadow-sm' : 'text-on-surface-variant hover:text-on-surface'}`}
              >
                {t('Mis Reservas')}
              </a>
              <a
                onClick={() => { setTab('seller'); setShowAll(false); }}
                className={`flex-1 mobile-sm:flex-none px-6 py-1.5 rounded-lg text-center text-sm font-bold cursor-pointer transition-colors ${tab === 'seller' ? 'bg-surface-container-lowest text-primary shadow-sm' : 'text-on-surface-variant hover:text-on-surface'}`}
              >
                {t('Ventas')}
              </a>
              <a
                onClick={() => { setTab('history'); setShowAll(false); }}
                className={`flex-1 mobile-sm:flex-none px-6 py-1.5 rounded-lg text-center text-sm font-bold cursor-pointer transition-colors ${tab === 'history' ? 'bg-surface-container-lowest text-primary shadow-sm' : 'text-on-surface-variant hover:text-on-surface'}`}
              >
                {t('Historial')}
              </a>
            </div>
            <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-center gap-2 mobile-sm:gap-3 px-2 mobile-sm:px-4 mobile-md:ml-auto">
              <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                <span className="material-symbols-outlined text-base">filter_list</span>
                <span>{t('Filtrar por')}:</span>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setShowAll(false); }}
                className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 py-0 cursor-pointer"
              >
                <option value="ALL">{t('Todos los estados')}</option>
                <option value="ACTIVE">{t('Pendiente')}</option>
                <option value="ACCEPTED">{t('Completada')}</option>
                <option value="REJECTED">{t('Rechazada')}</option>
                <option value="WITHDRAWN">{t('Cancelada')}</option>
              </select>
            </div>
          </div>

          {/* ── Spotify-style List ── */}
          <div className="bg-surface border border-outline rounded-xl overflow-hidden">
            {/* Table Header */}
            <div className="hidden tablet-lg:grid grid-cols-[48px_1.8fr_200px_140px_200px_120px] gap-4 px-6 py-3 border-b border-outline-variant/10 text-xs uppercase tracking-widest font-black text-on-surface-variant/60">
              <span>#</span>
              <span>{t('Producto')}</span>
              <span>{tab === 'seller' ? t('Comprador') : tab === 'buyer' ? t('Vendedor') : t('Contraparte')}</span>
              <span>{t('Estado')}</span>
              <span className="text-center">{t('Acciones')}</span>
              <span className="text-center">{t('Contactar')}</span>
            </div>

            {/* Rows */}
            {isLoading ? (
              <>
                <div className="tablet-lg:hidden divide-y divide-outline-variant/10">
                  {[1, 2, 3].map(i => (
                    <div key={`mobile-${i}`} className="p-4 animate-pulse space-y-3">
                      <div className="h-5 bg-surface-container-highest rounded w-2/3" />
                      <div className="h-4 bg-surface-container-highest rounded w-1/3" />
                      <div className="h-20 bg-surface-container-highest rounded-xl" />
                    </div>
                  ))}
                </div>
                <div className="hidden tablet-lg:block divide-y divide-outline-variant/10">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="grid grid-cols-[48px_1.8fr_200px_140px_200px_120px] gap-4 px-6 py-4 items-center animate-pulse">
                      <div className="h-3 bg-surface-container-highest rounded w-6" />
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-surface-container-highest" />
                        <div className="h-3 bg-surface-container-highest rounded w-32" />
                      </div>
                      <div className="h-3 bg-surface-container-highest rounded w-20" />
                      <div className="h-3 bg-surface-container-highest rounded w-16" />
                      <div className="h-3 bg-surface-container-highest rounded w-20" />
                      <div className="h-3 bg-surface-container-highest rounded w-16" />
                    </div>
                  ))}
                </div>
              </>
            ) : visibleList.length === 0 ? (
              <div className="py-16 flex flex-col items-center justify-center text-center">
                <span className="material-symbols-outlined text-5xl text-outline-variant mb-4">swap_horiz</span>
                <p className="text-sm font-bold text-on-surface mb-1">{t('No se encontraron compraventas')}</p>
                <p className="text-xs text-on-surface-variant">
                  {tab === 'buyer'
                    ? t('Reserva un producto para verlo aquí.')
                    : tab === 'seller'
                      ? t('Las reservas entrantes aparecerán aquí.')
                      : t('Las compraventas finalizadas, rechazadas o canceladas aparecerán acá.')}
                </p>
              </div>
            ) : (
              <>
                <div className="tablet-lg:hidden grid grid-cols-1 mobile-sm:grid-cols-2 gap-4 p-4">
                  {visibleList.map((res) => {
                    const product = res.product || {};
                    const productName = product.name || 'Product';
                    const imageUrl = getImageUrl(product.imageId);
                    const communityName = product.communityName || '';
                    const status = STATUS_CONFIG[res.status] || STATUS_CONFIG.ACTIVE;
                    const iAmBuyer = res.userId === currentUserId;
                    const partnerName = res.partnerName
                      || (iAmBuyer
                        ? (res.sellerName || `User #${product.publisherId || '?'}`)
                        : `User #${res.userId || '?'}`);
                    const partnerLabel = iAmBuyer ? t('Vendedor') : t('Comprador');
                    const otherUserId = res.partnerId || (iAmBuyer ? product.publisherId : res.userId);

                    const isPending = res.status === 'ACTIVE';
                    const canSellerAct = !iAmBuyer && isPending;
                    const canBuyerWithdraw = iAmBuyer && isPending;
                    const canChat = (isPending || res.status === 'ACCEPTED') && otherUserId;
                    const isHighlighted = highlightedId === res.id;
                    return (
                      <div
                        key={`mobile-${res.id}`}
                        ref={isHighlighted ? highlightedRowRef : null}
                        className="rounded-xl bg-surface border border-outline p-4 shadow-sm cursor-pointer"
                        style={isHighlighted ? { animation: 'cm-row-highlight-fade 2s ease-out forwards' } : undefined}
                        onClick={() => navigate(`/products/${product.id || res.productId}`)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-base font-bold text-on-surface truncate">{productName}</p>
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold uppercase ${status.color}`}>
                            <span
                              className="material-symbols-outlined text-base"
                              style={status.fill ? { fontVariationSettings: "'FILL' 1" } : undefined}
                            >
                              {status.icon}
                            </span>
                            {status.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-4">
                          {imageUrl ? (
                            <img className="w-14 h-14 rounded-xl object-cover bg-surface-container-highest shrink-0" src={imageUrl} alt="" />
                          ) : (
                            <div className="w-14 h-14 rounded-xl bg-surface-container-highest flex items-center justify-center shrink-0">
                              <span className="material-symbols-outlined text-base text-on-surface-variant">shopping_bag</span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs text-on-surface-variant uppercase tracking-widest font-bold">
                              {partnerLabel}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="w-6 h-6 rounded-full bg-primary-container text-[10px] flex items-center justify-center font-bold text-on-primary-container shrink-0">
                                {getInitials(partnerName)}
                              </div>
                              <span className="text-base font-medium text-on-surface truncate">{partnerName}</span>
                            </div>
                          </div>
                        </div>
                        <div className="pt-3 mt-3 border-t border-outline-variant/10 flex flex-col gap-3" onClick={e => e.stopPropagation()}>
                          {(canSellerAct || canBuyerWithdraw) && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant/60 mb-1.5">{t('Acciones')}</p>
                              <div className="flex flex-wrap gap-2">
                                {canSellerAct && (
                                  <>
                                    <button
                                      onClick={() => acceptMutation.mutate(res.id)}
                                      disabled={acceptMutation.isLoading}
                                      className="px-3 py-2 rounded-lg bg-primary text-on-primary inline-flex items-center gap-1 text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
                                    >
                                      <span className="material-symbols-outlined text-sm">check</span>
                                      {t('Aceptar')}
                                    </button>
                                    <button
                                      onClick={() => rejectMutation.mutate(res.id)}
                                      disabled={rejectMutation.isLoading}
                                      className="px-3 py-2 rounded-lg border border-error/40 text-error inline-flex items-center gap-1 text-sm font-bold cursor-pointer hover:bg-error/5 transition-colors disabled:opacity-60"
                                    >
                                      <span className="material-symbols-outlined text-sm">close</span>
                                      {t('Rechazar')}
                                    </button>
                                  </>
                                )}
                                {canBuyerWithdraw && (
                                  <button
                                    onClick={() => withdrawMutation.mutate(res.id)}
                                    disabled={withdrawMutation.isLoading}
                                    className="px-3 py-2 rounded-lg border border-error/40 text-error inline-flex items-center gap-1 text-sm font-bold cursor-pointer hover:bg-error/5 transition-colors disabled:opacity-60"
                                  >
                                    <span className="material-symbols-outlined text-sm">cancel</span>
                                    {t('Cancelar')}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          {canChat && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant/60 mb-1.5">{t('Contactar')}</p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => navigate(buildGoMessagesPath({
                                    userId: otherUserId,
                                    reservationId: res.id,
                                  }))}
                                  className="px-3 py-2 rounded-lg bg-primary text-on-primary inline-flex items-center gap-1 text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity"
                                >
                                  <span className="material-symbols-outlined text-sm">forum</span>
                                  {t('Abrir chat')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="hidden tablet-lg:block divide-y divide-outline-variant/10">
                  {visibleList.map((res, idx) => {
                    const product = res.product || {};
                    const productName = product.name || 'Product';
                    const imageUrl = getImageUrl(product.imageId);
                    const communityName = product.communityName || '';
                    const status = STATUS_CONFIG[res.status] || STATUS_CONFIG.ACTIVE;
                    const iAmBuyer = res.userId === currentUserId;
                    const partnerName = res.partnerName
                      || (iAmBuyer
                        ? (res.sellerName || `User #${product.publisherId || '?'}`)
                        : `User #${res.userId || '?'}`);
                    const otherUserId = res.partnerId || (iAmBuyer ? product.publisherId : res.userId);

                    const isPending = res.status === 'ACTIVE';
                    const canSellerAct = !iAmBuyer && isPending;
                    const canBuyerWithdraw = iAmBuyer && isPending;
                    const canChat = (isPending || res.status === 'ACCEPTED') && otherUserId;
                    const isHighlighted = highlightedId === res.id;
                    return (
                      <div
                        key={res.id}
                        ref={isHighlighted ? highlightedRowRef : null}
                        className="grid grid-cols-[48px_1.8fr_200px_140px_200px_120px] gap-4 px-6 py-4 items-center group cursor-pointer hover:bg-surface-container"
                        style={isHighlighted ? { animation: 'cm-row-highlight-fade 2s ease-out forwards' } : undefined}
                        onClick={() => navigate(`/products/${product.id || res.productId}`)}
                      >
                        <span className="text-base font-bold text-on-surface-variant/50">
                          {String(idx + 1).padStart(2, '0')}
                        </span>

                        <div className="flex items-center gap-3 min-w-0">
                          {imageUrl ? (
                            <img className="w-12 h-12 rounded-xl object-cover bg-surface-container-highest shrink-0" src={imageUrl} alt="" />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-surface-container-highest flex items-center justify-center shrink-0">
                              <span className="material-symbols-outlined text-base text-on-surface-variant">shopping_bag</span>
                            </div>
                          )}
                          <span className="text-base font-bold text-on-surface truncate">{productName}</span>
                        </div>

                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-primary-container text-[10px] flex items-center justify-center font-bold text-on-primary-container shrink-0">
                            {getInitials(partnerName)}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-on-surface truncate">{partnerName}</span>
                            {tab === 'history' && (
                              <span className="text-[10px] text-on-surface-variant uppercase tracking-wider font-bold flex items-center gap-1">
                                <span className="material-symbols-outlined text-[12px]">{iAmBuyer ? 'shopping_cart' : 'sell'}</span>
                                {iAmBuyer ? t('Le compré') : t('Le vendí')}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className={`flex items-center gap-2 ${status.color}`}>
                          <span
                            className="material-symbols-outlined text-lg"
                            style={status.fill ? { fontVariationSettings: "'FILL' 1" } : undefined}
                          >
                            {status.icon}
                          </span>
                          <span className="text-xs font-bold uppercase tracking-tight">{status.label}</span>
                        </div>

                        <div className="flex justify-center items-center gap-1.5 flex-wrap" onClick={e => e.stopPropagation()}>
                          {canSellerAct && (
                            <>
                              <button
                                onClick={() => acceptMutation.mutate(res.id)}
                                disabled={acceptMutation.isLoading}
                                className="px-2.5 py-1.5 rounded-lg bg-primary text-on-primary hover:opacity-90 transition-opacity inline-flex items-center gap-1 text-xs font-bold cursor-pointer disabled:opacity-60"
                                title={t('Aceptar reserva')}
                              >
                                <span className="material-symbols-outlined text-sm">check</span>
                                {t('Aceptar')}
                              </button>
                              <button
                                onClick={() => rejectMutation.mutate(res.id)}
                                disabled={rejectMutation.isLoading}
                                className="px-2.5 py-1.5 rounded-lg border border-error/40 text-error hover:bg-error/5 transition-colors inline-flex items-center gap-1 text-xs font-bold cursor-pointer disabled:opacity-60"
                                title={t('Rechazar')}
                              >
                                <span className="material-symbols-outlined text-sm">close</span>
                                {t('Rechazar')}
                              </button>
                            </>
                          )}
                          {canBuyerWithdraw && (
                            <button
                              onClick={() => withdrawMutation.mutate(res.id)}
                              disabled={withdrawMutation.isLoading}
                              className="px-2.5 py-1.5 rounded-lg border border-error/40 text-error hover:bg-error/5 transition-colors inline-flex items-center gap-1 text-xs font-bold cursor-pointer disabled:opacity-60"
                              title={t('Cancelar reserva')}
                            >
                              <span className="material-symbols-outlined text-sm">cancel</span>
                              {t('Cancelar')}
                            </button>
                          )}
                        </div>

                        <div className="flex justify-center items-center gap-1.5" onClick={e => e.stopPropagation()}>
                          {canChat && (
                            <button
                              onClick={() => navigate(buildGoMessagesPath({
                                userId: otherUserId,
                                reservationId: res.id,
                              }))}
                              className="px-3 py-2 rounded-lg bg-primary text-on-primary hover:opacity-90 transition-opacity inline-flex items-center gap-1 text-xs font-bold cursor-pointer"
                              title={t('Abrir chat')}
                            >
                              <span className="material-symbols-outlined text-base">forum</span>
                              {t('Abrir chat')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Footer */}
            {hasMore && (
              <div className="p-4 flex items-center justify-center border-t border-outline-variant/10">
                <a
                  onClick={() => setShowAll((prev) => !prev)}
                  className="text-[10px] uppercase tracking-widest font-black text-on-surface-variant hover:text-primary transition-colors flex items-center gap-2 cursor-pointer"
                >
                  {showAll ? t('Ver menos') : `${t('Ver todos')} (${filtered.length})`}
                  <span className="material-symbols-outlined text-sm">{showAll ? 'expand_less' : 'expand_more'}</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default ReservationsNew;
